import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Loader2, Pencil, ShieldAlert, ShieldCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { AdminAPI, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useFormatters } from "@/lib/format";

function StatusBadge({ status }) {
    if (status === "suspended") {
        return (
            <Badge data-testid="detail-status-suspended"
                    className="border-amber-300 bg-amber-100 text-amber-800 hover:bg-amber-100">
                Suspended
            </Badge>
        );
    }
    return (
        <Badge data-testid="detail-status-active"
                className="border-emerald-300 bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
            Active
        </Badge>
    );
}

export default function AdminTenantDetailPage() {
    const { id } = useParams();
    const nav = useNavigate();
    const { startImpersonation } = useAuth();
    const { formatDateTime, formatDate } = useFormatters();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editingName, setEditingName] = useState(false);
    const [draftName, setDraftName] = useState("");
    const [confirmImp, setConfirmImp] = useState(false);
    const [impPending, setImpPending] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await AdminAPI.getTenant(id);
            setData(r);
            setDraftName(r.name);
        } catch (err) {
            toast.error(extractError(err));
        } finally {
            setLoading(false);
        }
    }, [id]);
    useEffect(() => { load(); }, [load]);

    async function toggleStatus() {
        if (!data) return;
        const next = data.status === "suspended" ? "active" : "suspended";
        try {
            await AdminAPI.updateTenant(id, { status: next });
            toast.success(`Tenant is now ${next}`);
            load();
        } catch (err) {
            toast.error(extractError(err));
        }
    }

    async function saveName() {
        if (!draftName.trim() || draftName === data.name) {
            setEditingName(false); return;
        }
        try {
            await AdminAPI.updateTenant(id, { name: draftName.trim() });
            toast.success("Name updated");
            setEditingName(false);
            load();
        } catch (err) {
            toast.error(extractError(err));
        }
    }

    async function doImpersonate() {
        setImpPending(true);
        const r = await startImpersonation(id);
        setImpPending(false);
        if (r.ok) {
            toast.success(`Impersonating ${data.name}`);
            nav("/", { replace: true });
        } else {
            toast.error(r.message || "Impersonation failed");
        }
        setConfirmImp(false);
    }

    if (loading) {
        return (
            <div className="grid place-items-center py-20 text-zinc-500" data-testid="tenant-detail-loading">
                <Loader2 className="h-5 w-5 animate-spin" />
            </div>
        );
    }
    if (!data) return null;

    return (
        <div className="space-y-6" data-testid="admin-tenant-detail-page">
            <div>
                <Link to="/admin/tenants" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200"
                       data-testid="back-to-tenants">
                    <ChevronLeft className="h-3 w-3" />
                    Back to tenants
                </Link>
            </div>

            <header className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-1 items-center gap-3">
                    {editingName ? (
                        <div className="flex w-full max-w-md items-center gap-2">
                            <Input
                                value={draftName}
                                onChange={(e) => setDraftName(e.target.value)}
                                data-testid="tenant-name-input"
                            />
                            <Button size="sm" onClick={saveName} data-testid="tenant-name-save">Save</Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingName(false)}>Cancel</Button>
                        </div>
                    ) : (
                        <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="tenant-name">
                            {data.name}
                            <button onClick={() => setEditingName(true)}
                                     className="ml-2 inline-flex items-center text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                                     data-testid="tenant-name-edit">
                                <Pencil className="h-3.5 w-3.5" />
                            </button>
                        </h1>
                    )}
                    <StatusBadge status={data.status} />
                </div>
                <div className="flex gap-2">
                    <Button onClick={() => setConfirmImp(true)} variant="outline" data-testid="detail-impersonate-btn">
                        <ShieldAlert className="mr-1 h-3.5 w-3.5" />
                        Impersonate
                    </Button>
                    <Button onClick={toggleStatus} variant="ghost" data-testid="detail-toggle-status">
                        {data.status === "suspended" ? (
                            <><ShieldCheck className="mr-1 h-3.5 w-3.5" />Activate</>
                        ) : "Suspend"}
                    </Button>
                </div>
            </header>

            <p className="font-mono text-[11px] text-zinc-500">slug · {data.slug} · created {data.created_at ? formatDate(data.created_at) : "—"}</p>

            <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="integrity-grid">
                {Object.entries(data.integrity || {}).map(([k, v]) => (
                    <Card key={k} className="p-4">
                        <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                            {k.replace(/_/g, " ")}
                        </div>
                        <div className="mt-1 font-display text-2xl font-bold tabular-nums">{v}</div>
                    </Card>
                ))}
            </section>

            <section>
                <h2 className="mb-2 font-display text-lg font-bold tracking-tight">Branches</h2>
                <Card className="overflow-hidden" data-testid="branches-table">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Code</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>State</TableHead>
                                <TableHead>Currency</TableHead>
                                <TableHead>Timezone</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(data.branches || []).length === 0 ? (
                                <TableRow><TableCell colSpan={5} className="py-6 text-center text-sm text-zinc-500">No branches.</TableCell></TableRow>
                            ) : data.branches.map((b) => (
                                <TableRow key={b.id} data-testid={`branch-row-${b.code}`}>
                                    <TableCell className="font-mono text-xs">{b.code}</TableCell>
                                    <TableCell>{b.name}</TableCell>
                                    <TableCell className="font-mono text-xs">{b.state_code}</TableCell>
                                    <TableCell className="font-mono text-xs">{b.currency}</TableCell>
                                    <TableCell className="font-mono text-xs">{b.timezone}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            </section>

            <section>
                <h2 className="mb-2 font-display text-lg font-bold tracking-tight">Users</h2>
                <Card className="overflow-hidden" data-testid="users-table">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Email</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Role</TableHead>
                                <TableHead>Active</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(data.users || []).length === 0 ? (
                                <TableRow><TableCell colSpan={4} className="py-6 text-center text-sm text-zinc-500">No users.</TableCell></TableRow>
                            ) : data.users.map((u) => (
                                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                                    <TableCell className="font-mono text-xs">{u.email}</TableCell>
                                    <TableCell>{u.name}</TableCell>
                                    <TableCell>
                                        <Badge className="border-zinc-300 bg-zinc-100 text-zinc-700 hover:bg-zinc-100">
                                            {u.role || "—"}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        {u.is_active ? (
                                            <span className="font-mono text-xs text-emerald-700">yes</span>
                                        ) : (
                                            <span className="font-mono text-xs text-zinc-400">no</span>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            </section>

            <Dialog open={confirmImp} onOpenChange={setConfirmImp}>
                <DialogContent data-testid="detail-impersonate-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-700">
                            <ShieldAlert className="h-5 w-5" />
                            Confirm impersonation
                        </DialogTitle>
                        <DialogDescription>
                            You are about to impersonate <strong>{data.name}</strong> as the tenant owner.
                            All actions are logged. The token expires in 15 minutes.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setConfirmImp(false)}>Cancel</Button>
                        <Button onClick={doImpersonate} disabled={impPending}
                                 data-testid="detail-imp-confirm"
                                 className="bg-red-600 text-white hover:bg-red-700">
                            {impPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Yes, impersonate
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
